import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { environment } from 'src/environments/environment';
import { UserService } from './user.service';

export interface WebSocketNotification {
  type: 'global' | 'project';
  event: string;
  data: any;
  project_id?: string;
  timestamp: string;
}

export interface ProjectNotification extends WebSocketNotification {
  type: 'project';
  project_id: string;
  event:
    | 'share_role_change'
    | 'area_added'
    | 'area_edited'
    | 'area_removed'
    | 'project_added'
    | 'project_edited'
    | 'project_removed'
    | 'download_completed'
    | 'report_completed'
    | 'upload_progress'
    | 'upload_completed'
    | 'upload_failed';
}

export interface GlobalNotification extends WebSocketNotification {
  type: 'global';
  event: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketNotificationService implements OnDestroy {
  private wsSubject: WebSocketSubject<any> | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 5000; // 5 seconds

  // Subjects for different types of notifications
  private globalNotificationsSubject = new Subject<GlobalNotification>();
  private projectNotificationsSubject = new Subject<ProjectNotification>();
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);

  // Public observables
  public globalNotifications$ = this.globalNotificationsSubject.asObservable();
  public projectNotifications$ =
    this.projectNotificationsSubject.asObservable();
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  constructor(private userService: UserService) {
    this.connect();
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  private connect(): void {
    if (this.isConnected || !this.userService.currentAccessToken) {
      return;
    }

    try {
      const wsUrl = `${environment.wsBaseUrl}?token=${this.userService.currentAccessToken}`;
      this.wsSubject = webSocket({
        url: wsUrl,
        openObserver: {
          next: () => {
            console.log('WebSocket connected to notification server');
            this.isConnected = true;
            this.reconnectAttempts = 0;
            this.connectionStatusSubject.next(true);
          }
        },
        closeObserver: {
          next: () => {
            console.log('WebSocket disconnected from notification server');
            this.isConnected = false;
            this.connectionStatusSubject.next(false);
            this.handleReconnect();
          }
        }
      });

      this.wsSubject.subscribe({
        next: (message: WebSocketNotification) => {
          this.handleMessage(message);
        },
        error: (error) => {
          console.error('WebSocket error:', error);
          this.isConnected = false;
          this.connectionStatusSubject.next(false);

          // Don't retry on certain error types (like 404, 401, 403)
          if (this.shouldRetryConnection(error)) {
            this.handleReconnect();
          } else {
            console.error('WebSocket connection failed permanently:', error);
          }
        }
      });
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      if (this.shouldRetryConnection(error)) {
        this.handleReconnect();
      } else {
        console.error('WebSocket connection failed permanently:', error);
      }
    }
  }

  private handleMessage(message: WebSocketNotification): void {
    console.log('Received WebSocket notification:', message);

    switch (message.type) {
      case 'global':
        this.globalNotificationsSubject.next(message as GlobalNotification);
        break;
      case 'project':
        this.projectNotificationsSubject.next(message as ProjectNotification);
        break;
      default:
        console.warn('Unknown notification type:', message.type);
    }
  }

  private shouldRetryConnection(error: any): boolean {
    // Don't retry on authentication errors or not found errors
    if (error?.code === 1006 || error?.code === 1002 || error?.code === 1003) {
      return false; // Connection closed, protocol error, unsupported data
    }

    // Don't retry on HTTP-like errors in WebSocket context
    if (
      error?.status === 401 ||
      error?.status === 403 ||
      error?.status === 404
    ) {
      return false;
    }

    // Don't retry if we've already reached max attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return false;
    }

    return true;
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.isConnected = false;
      this.connectionStatusSubject.next(false);
      return;
    }

    this.reconnectAttempts++;
    console.log(
      `Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`
    );

    setTimeout(() => {
      if (this.reconnectAttempts <= this.maxReconnectAttempts) {
        this.connect();
      }
    }, this.reconnectInterval);
  }

  private disconnect(): void {
    if (this.wsSubject) {
      this.wsSubject.complete();
      this.wsSubject = null;
    }
    this.isConnected = false;
    this.connectionStatusSubject.next(false);
  }

  public reconnect(): void {
    this.disconnect();
    this.reconnectAttempts = 0;
    this.connect();
  }

  public isWebSocketConnected(): boolean {
    return this.isConnected;
  }

  // Method to subscribe to global notifications
  public subscribeToGlobal(): Observable<GlobalNotification> {
    return this.globalNotifications$;
  }
}
